import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { color } from 'styled-system';
import ScrollAdapt from '~/components/misc/scroll-adapt.js';
import breakpoint, { getZindexByType } from '~/utils/style-utils.ts';
import useUIContext from '~/ui/use-ui-context';
import { selectHeadroomIsForced } from '~/components/scroll/ducks/index.js';
import useSearchLocation from '~/hooks/use-search-location';
import useDisplayGlobalNavigation from '~/components/global-navigation/use-display-global-navigation';

// calc top based on regulations and top nav
const searchBarTop = ({ hasStaticHeader, hasTopNavigation, hasNativeHeader }) => {
	const staticHeaderHeight = hasStaticHeader ? 'var(--regulationHeight)' : '0px';
	// Don't add top navigation height when native header is enabled
	const topNavHeight = hasTopNavigation && !hasNativeHeader ? 'var(--topNavHeight)' : '0px';
	return `calc(${staticHeaderHeight} + ${topNavHeight} + var(--smallerHeaderHeight))`;
};

const searchBarBackground = ({ hasTopNavigation }) => {
	return hasTopNavigation ? 'var(--subnavigation-bar-bg)' : 'var(--head-bg, rgba(var(--primary-dark), 1))';
};

// Before added to hide gap between search bar and
// header on scroll.
const Wrapper = styled(ScrollAdapt)`
	position: sticky;
	top: ${({ uiContext }) => searchBarTop(uiContext)};
	background-color: ${({ uiContext }) => searchBarBackground(uiContext)};
	grid-area: search;
	display: grid;
	grid-template-columns: ${({ isSearchLocation }) => (isSearchLocation ? '1fr' : 'auto 1fr')};
	grid-gap: ${({ displayGlobalNavigation }) => (!displayGlobalNavigation ? '12px' : '0')};
	padding: 0 var(--smallerFrame);
	width: 100%;
	z-index: ${getZindexByType('small-product-nav')};
	align-items: center;
	justify-content: start;
	${color}

	&::before {
		content: '';
		position: absolute;
		background-color: var(--head-bg, rgba(var(--primary-dark), 1));
		height: 200%;
		top: -200%;
		width: 100%;
	}

	${breakpoint.lg`
		padding: 0;
	`}
`;

const transitionVisible = {
	duration: 0.2,
	type: 'tween',
	delay: 0.15
};

const transitionHidden = {
	duration: 0.1,
	type: 'tween'
};

// const transition = {
// 	ease: [0.76, 0.05, 0.98, 0.32],
// 	duration: 0.22
// };

const SearchBarWrapper = ({ children }) => {
	const uiContext = useUIContext();
	const navbarIsForced = useSelector(selectHeadroomIsForced);
	const { isSearchLocation } = useSearchLocation();
	const { displayGlobalNavigation } = useDisplayGlobalNavigation();

	const variants = useMemo(() => {
		return {
			visible: { y: 0, transition: transitionVisible },
			hidden: {
				y: `calc((${navbarIsForced ? '0px' : 'var(--smallerHeaderHeight)'}) * -1)`,
				transition: transitionHidden
			}
		};
	}, [navbarIsForced]);

	return (
		<Wrapper
			variants={variants}
			uiContext={uiContext}
			isSearchLocation={isSearchLocation}
			displayGlobalNavigation={displayGlobalNavigation}
		>
			{children}
		</Wrapper>
	);
};

export default SearchBarWrapper;
